import { useState } from 'react'
import { api } from '../api/client'
import type { BackupResult } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { useOperation } from '../hooks/useOperation'
import { useAuth } from '../auth'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { Badge, Empty, Notice, fmtTime } from '../components/ui'

const size = (n: number) => `${(n / 1024 / 1024).toFixed(2)} MiB`

// 备份是唯一的退路，低于 30 天的保留请求会被服务器抬回下限；
// 表单同样按这个值发送，避免操作员以为自己设的天数生效了。
const MIN_KEEP_DAYS = 30

export function System() {
  const { user } = useAuth()
  const [keepDays, setKeepDays] = useState(30)
  const [label, setLabel] = useState('manual')
  const [confirm, setConfirm] = useState(false)
  const op = useOperation<BackupResult>('backup')

  const { data, error, loading, reload } = useAsync(() => api.get<{ items: BackupResult[] }>('/api/backups'), [])
  const rows = data?.items ?? []

  async function run() {
    const keep = Math.max(MIN_KEEP_DAYS, keepDays)
    const res = await op.run((key) => api.post<BackupResult>('/api/backups', { keepDays: keep, label }, key))
    if (res) {
      setConfirm(false)
      reload()
    }
  }

  return (
    <section>
      <header className="page-head compact">
        <h1>系统 / 备份</h1>
        <button type="button" onClick={reload} disabled={loading}>
          {loading ? '载入中…' : '刷新'}
        </button>
      </header>

      <div className="card">
        <h2>手动备份</h2>
        <div className="row wrap">
          <label className="field">
            <span>保留天数</span>
            <input
              type="number"
              min={MIN_KEEP_DAYS}
              max={365}
              value={keepDays}
              onChange={(e) => setKeepDays(Number(e.target.value))}
            />
            <em>不得低于 {MIN_KEEP_DAYS} 天：低于下限的值会被抬到下限，超出的旧备份会被清掉。</em>
          </label>
          <label className="field">
            <span>标签</span>
            <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={40} />
          </label>
        </div>
        <div className="row end">
          <button
            type="button"
            className="primary"
            disabled={!user?.canWrite || op.busy}
            onClick={() => {
              op.newIntent()
              setConfirm(true)
            }}
          >
            立即备份
          </button>
        </div>
        <Notice error={op.error} />
        {op.result && (
          <div className="notice ok">
            <strong>备份完成，integrity_check {op.result.integrityOk ? '通过' : '失败'}</strong>
            <div className="sub mono">{op.result.name}</div>
          </div>
        )}
      </div>

      <div className="card table-card">
        <h2>备份文件</h2>
        <Notice error={error} />
        {!loading && rows.length === 0 && <Empty text="还没有备份。生产上每天 03:00 会自动生成。" />}
        {rows.length > 0 && (
          <table className="table">
            <thead>
              <tr>
                <th>时间 (UTC)</th>
                <th>文件</th>
                <th>大小</th>
                <th>完整性</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.name}>
                  <td className="muted">{fmtTime(b.createdAt)}</td>
                  <td className="mono small">{b.name}</td>
                  <td>{size(b.sizeBytes)}</td>
                  <td>
                    <Badge tone={b.integrityOk ? 'ok' : 'warn'}>{b.integrityOk ? 'OK' : 'UNKNOWN'}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="muted">
          恢复流程：停服 → 备份现库 → 用 <code>VACUUM INTO</code> 产物替换 → 启动并跑 <code>drill.sh</code> 校验。
          恢复属于运维动作，只在 Ubuntu 主机上执行。
        </p>
      </div>

      {confirm && (
        <ConfirmDialog
          title="确认手动备份"
          description={`将把当前数据库完整快照写入备份目录，并按 ${keepDays} 天清理旧文件。`}
          expectText="备份"
          confirmLabel="开始备份"
          busy={op.busy}
          error={op.error}
          onConfirm={() => void run()}
          onClose={() => {
            setConfirm(false)
            op.newIntent()
          }}
        />
      )}
    </section>
  )
}
